import { Employee } from "@msidata/service-pro-api-schema";
import _ from "lodash";
import { ajax } from "../../app/core/ajax";
import { getAll } from "../../app/core/getAll";
import * as z from "../../framework";
import { getLocalStorage, setLocalStorage } from "../core/core";

interface UserCache<T = string> {
    [key: number]: T;
}
let userFirstNames: UserCache = {};
let userLastNames: UserCache = {};
let userEmails: UserCache = {};
let userTelephoneNumbers: UserCache = {};
let userLastActivityDates: UserCache = {};
let userDivisionIds: UserCache<number> = {};
let userDefaultRegionIds: UserCache<number> = {};

export function getEmailAndFullName(
    id: number
): { Name: string; Email: string } {
    return {
        Name: z.util.buildFullName(userFirstNames[id], userLastNames[id]),
        Email: userEmails[id] || "---"
    };
}

export function getFullName(id: number) {
    return z.util.buildFullName(userFirstNames[id], userLastNames[id], "---");
}

export function getFirstName(id: number) {
    return userFirstNames[id] || "---";
}

export function getLastName(id: number) {
    return userLastNames[id] || "---";
}

export function getEmail(id: number) {
    return userEmails[id] || "---";
}

export function getTelephoneNumber(id: number) {
    return userTelephoneNumbers[id] || "---";
}

export function getLastActivityDate(id: number) {
    return userLastActivityDates[id];
}

export function getDivisionId(id: number) {
    return userDivisionIds[id];
}

export function getDefaultRegionId(id: number) {
    return userDefaultRegionIds[id];
}

export async function loadUserCache() {
    try {
        if (
            localStorage.userEmails &&
            localStorage.userFirstNames &&
            localStorage.userLastNames &&
            localStorage.userTelephoneNumbers &&
            localStorage.userLastActivityDates &&
            localStorage.userDivisionIds &&
            localStorage.userDefaultRegionIds
        ) {
            userEmails = $.parseJSON(getLocalStorage("userEmails"));
            userFirstNames = $.parseJSON(getLocalStorage("userFirstNames"));
            userLastNames = $.parseJSON(getLocalStorage("userLastNames"));
            userTelephoneNumbers = $.parseJSON(
                getLocalStorage("userTelephoneNumbers")
            );
            userLastActivityDates = $.parseJSON(
                getLocalStorage("userLastActivityDates")
            );
            userDivisionIds = $.parseJSON(getLocalStorage("userDivisionIds"));
            userDefaultRegionIds = $.parseJSON(
                getLocalStorage("userDefaultRegionIds")
            );
        } else {
            const data: Partial<Employee>[] = await getAll({
                entity: "Employee",
                select:
                    "UserId,EmailAddress,FirstName,LastName,TelephoneNumber,LastActivityDate,DivisionId,DefaultRegionId"
            });
            userEmails = _(data)
                .keyBy("UserId")
                .mapValues("EmailAddress")
                .value() as any;
            setLocalStorage("userEmails", JSON.stringify(userEmails));

            userFirstNames = _(data)
                .keyBy("UserId")
                .mapValues("FirstName")
                .value() as any;
            setLocalStorage("userFirstNames", JSON.stringify(userFirstNames));

            userLastNames = _(data)
                .keyBy("UserId")
                .mapValues("LastName")
                .value() as any;
            setLocalStorage("userLastNames", JSON.stringify(userLastNames));

            userTelephoneNumbers = _(data)
                .keyBy("UserId")
                .mapValues("TelephoneNumber")
                .value() as any;
            setLocalStorage(
                "userTelephoneNumbers",
                JSON.stringify(userTelephoneNumbers)
            );

            userLastActivityDates = _(data)
                .keyBy("UserId")
                .mapValues("LastActivityDate")
                .value() as any;
            setLocalStorage(
                "userLastActivityDates",
                JSON.stringify(userLastActivityDates)
            );

            userDivisionIds = _(data)
                .keyBy("UserId")
                .mapValues("DivisionId")
                .value() as any;
            setLocalStorage("userDivisionIds", JSON.stringify(userDivisionIds));

            userDefaultRegionIds = _(data)
                .keyBy("UserId")
                .mapValues("DefaultRegionId")
                .value() as any;
            setLocalStorage(
                "userDefaultRegionIds",
                JSON.stringify(userDefaultRegionIds)
            );
        }
        return _.keys(userEmails);
    } catch (e) {
        console.log("Error loading user cache");
        return [];
    }
}

export async function updateUser(id: number): Promise<number> {
    const data = await ajax({
        url: `Employee/${id}?$select=UserId,EmailAddress,FirstName,LastName,TelephoneNumber,LastActivityDate,DivisionId,DefaultRegionId`
    });

    var employee = data.d.results;

    userEmails[employee.UserId] = employee.EmailAddress;
    setLocalStorage("userEmails", JSON.stringify(userEmails));

    userFirstNames[employee.UserId] = employee.FirstName;
    setLocalStorage("userFirstNames", JSON.stringify(userFirstNames));

    userLastNames[employee.UserId] = employee.LastName;
    setLocalStorage("userLastNames", JSON.stringify(userLastNames));

    userTelephoneNumbers[employee.UserId] = employee.TelephoneNumber;
    setLocalStorage(
        "userTelephoneNumbers",
        JSON.stringify(userTelephoneNumbers)
    );

    userLastActivityDates[employee.UserId] = employee.LastActivityDate;
    setLocalStorage(
        "userLastActivityDates",
        JSON.stringify(userLastActivityDates)
    );

    userDivisionIds[employee.UserId] = employee.DivisionId;
    setLocalStorage("userDivisionIds", JSON.stringify(userDivisionIds));

    userDefaultRegionIds[employee.UserId] = employee.DefaultRegionId;
    setLocalStorage(
        "userDefaultRegionIds",
        JSON.stringify(userDefaultRegionIds)
    );

    return employee.UserId;
}

export async function deleteUser(id: number): Promise<number> {
    const data = await ajax({
        url: `Employee/All/${id}?$select=UserId`
    });

    var employee = data.d.results;

    delete userEmails[employee.UserId];
    setLocalStorage("userEmails", JSON.stringify(userEmails));

    delete userFirstNames[employee.UserId];
    setLocalStorage("userFirstNames", JSON.stringify(userFirstNames));

    delete userLastNames[employee.UserId];
    setLocalStorage("userLastNames", JSON.stringify(userLastNames));

    delete userTelephoneNumbers[employee.UserId];
    setLocalStorage(
        "userTelephoneNumbers",
        JSON.stringify(userTelephoneNumbers)
    );

    delete userLastActivityDates[employee.UserId];
    setLocalStorage(
        "userLastActivityDates",
        JSON.stringify(userLastActivityDates)
    );

    delete userDivisionIds[employee.UserId];
    setLocalStorage("userDivisionIds", JSON.stringify(userDivisionIds));

    delete userDefaultRegionIds[employee.UserId];
    setLocalStorage(
        "userDefaultRegionIds",
        JSON.stringify(userDefaultRegionIds)
    );

    return employee.UserId;
}
